import type { UserBillingData } from "../../api/admin-revenue";
import {
  Td,
  Th,
  fmtDate,
  shortId,
  tableStyles,
} from "../../components/DataTable";
import { t } from "../../i18n";

export function LedgerTable({
  entries,
}: {
  entries: UserBillingData["recent_ledger"];
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.token_ledger")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_type")}</Th>
            <Th>{t("billing_table.col_amount")}</Th>
            <Th>{t("billing_table.col_balance_after")}</Th>
            <Th>{t("billing_table.col_description")}</Th>
            <Th>{t("billing_table.col_date")}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td>{e.entry_type}</Td>
              <Td>{e.amount}</Td>
              <Td>{e.balance_after}</Td>
              <Td>{e.description ?? "-"}</Td>
              <Td>{fmtDate(e.created_at)}</Td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("billing_table.no_ledger_entries")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export function UsageEventsTable({
  entries,
}: {
  entries: UserBillingData["recent_usage_events"];
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.usage_events")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_source")}</Th>
            <Th>{t("billing_table.col_resource")}</Th>
            <Th>{t("billing_table.col_provider_model")}</Th>
            <Th>{t("billing_table.col_status")}</Th>
            <Th>{t("billing_table.col_units")}</Th>
            <Th>{t("billing_table.col_price_version")}</Th>
            <Th>{t("billing_table.col_finalized")}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td>{e.source}</Td>
              <Td>{e.resource_type}</Td>
              <Td>{formatProviderModel(e.provider, e.model)}</Td>
              <Td>
                <StatusBadge status={e.status} />
              </Td>
              <Td>{e.billable_units}</Td>
              <Td>{e.price_version}</Td>
              <Td>{formatNullableDate(e.finalized_at)}</Td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <Td colSpan={7}>{t("billing_table.no_usage_events")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export function PaymentTransactionsTable({
  entries,
}: {
  entries: UserBillingData["recent_payment_transactions"];
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.payment_transactions")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_provider")}</Th>
            <Th>{t("billing_table.col_sku")}</Th>
            <Th>{t("billing_table.col_status")}</Th>
            <Th>{t("billing_table.col_transaction")}</Th>
            <Th>{t("billing_table.col_entitlement")}</Th>
            <Th>{t("billing_table.col_verified")}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td>{`${e.provider}/${e.provider_environment}`}</Td>
              <Td>{e.hashee_sku_id}</Td>
              <Td>
                <StatusBadge status={e.status} />
              </Td>
              <Td mono>{shortId(e.provider_transaction_id)}</Td>
              <Td mono>{formatNullableId(e.entitlement_event_id)}</Td>
              <Td>{formatNullableDate(e.verified_at)}</Td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <Td colSpan={6}>{t("billing_table.no_payment_transactions")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export function EntitlementEventsTable({
  entries,
}: {
  entries: UserBillingData["recent_entitlement_events"];
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.entitlement_events")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_event_type")}</Th>
            <Th>{t("billing_table.col_plan")}</Th>
            <Th>{t("billing_table.col_token_delta")}</Th>
            <Th>{t("billing_table.col_storage_limit")}</Th>
            <Th>{t("billing_table.col_preview_minutes")}</Th>
            <Th>{t("billing_table.col_source_transaction")}</Th>
            <Th>{t("billing_table.col_effective")}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td>{e.event_type}</Td>
              <Td>{e.plan_type ?? "-"}</Td>
              <Td>{formatNullableNumber(e.token_credit_delta)}</Td>
              <Td>{formatNullableNumber(e.storage_limit_bytes)}</Td>
              <Td>{formatNullableNumber(e.preview_compute_minutes_delta)}</Td>
              <Td mono>{shortId(e.source_transaction_id)}</Td>
              <Td>{fmtDate(e.effective_at)}</Td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <Td colSpan={7}>{t("billing_table.no_entitlement_events")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export function WebhookTable({
  events,
  onReplay,
}: {
  events: UserBillingData["recent_webhooks"];
  onReplay: (id: string) => void;
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.webhook_events")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_event_id")}</Th>
            <Th>{t("billing_table.col_event_type")}</Th>
            <Th>{t("billing_table.col_status")}</Th>
            <Th>{t("billing_table.col_received")}</Th>
            <Th>{t("billing_table.col_processed")}</Th>
            <Th>{t("billing_table.col_action")}</Th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <Td mono>{shortId(e.event_id)}</Td>
              <Td>{e.event_type}</Td>
              <Td>
                <StatusBadge status={e.status} />
              </Td>
              <Td>{fmtDate(e.received_at)}</Td>
              <Td>{formatNullableDate(e.processed_at)}</Td>
              <Td>
                {e.status === "failed" && (
                  <button
                    type="button"
                    onClick={() => onReplay(e.id)}
                    style={s.replayBtn}
                  >
                    {t("billing_table.replay")}
                  </button>
                )}
              </Td>
            </tr>
          ))}
          {events.length === 0 && (
            <tr>
              <Td colSpan={6}>{t("billing_table.no_webhook_events")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* ---------- Internal ---------- */

const STATUS_COLORS: Record<string, React.CSSProperties> = {
  processed: { background: "#dcfce7", color: "#15803d" },
  failed: { background: "#fef2f2", color: "#dc2626" },
  pending: { background: "#fef9c3", color: "#a16207" },
  finalized: { background: "#dcfce7", color: "#15803d" },
  recorded: { background: "#dbeafe", color: "#1d4ed8" },
  reversed: { background: "#fef2f2", color: "#dc2626" },
};

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const color = STATUS_COLORS[status] ?? {};
  return <span style={{ ...s.badge, ...color }}>{status}</span>;
}

function formatProviderModel(
  provider: string | null,
  model: string | null,
): string {
  if (!provider && !model) return "-";
  return [provider, model].filter(Boolean).join(" / ");
}

function formatNullableDate(value: string | null): string {
  return value ? fmtDate(value) : "-";
}

function formatNullableId(value: string | null): string {
  return value ? shortId(value) : "-";
}

function formatNullableNumber(value: number | null): string {
  return value === null ? "-" : String(value);
}

/* ---------- Styles ---------- */

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  section: { marginBottom: 24, overflowX: "auto" },
  sectionTitle: { fontSize: 15, fontWeight: 600, marginBottom: 8 },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
  },
  replayBtn: {
    padding: "4px 10px",
    fontSize: 12,
    borderRadius: 4,
    border: "1px solid var(--color-danger)",
    color: "var(--color-danger)",
  },
};
